import React from 'react';
import { TDefaultFieldType } from '../components/types';
import { TValidationFunction } from '../types';
import { classToValidate, classToValidationFunction } from '../util';
import { validationsStore } from '../validationStore';
import { FormContext } from '../form/FormContext';

export function useValidationClass<T>({
  customValidation,
  formName,
  name,
  validationClass,
  validationFunction,
  validationRules,
}: Pick<
  TDefaultFieldType<T>,
  'validationClass' | 'validationFunction' | 'validationRules' | 'name'
> & {
  customValidation?: TValidationFunction<T>;
  formName?: string;
}) {
  const { name: contextFormName } = React.useContext(FormContext);

  const classesValidationFunction = React.useMemo(
    () =>
      validationClass !== undefined
        ? classToValidationFunction<T>(validationClass)
        : undefined,
    [validationClass],
  );
  const initialValidationFunction = React.useMemo(
    () =>
      validationsStore.getFieldStore(formName ?? contextFormName, name)
        ?.validationFunction,
    [contextFormName, formName, name],
  );
  const actualValidationFunction: TValidationFunction<T> = React.useCallback(
    (currentValue, parameterFormName) => {
      let result =
        initialValidationFunction?.(currentValue, parameterFormName) ??
        validationFunction?.(currentValue, parameterFormName) ??
        true;
      if (typeof result === 'string') return result;

      if (customValidation) {
        result = customValidation(currentValue, parameterFormName);
      }
      if (typeof result === 'string') return result;

      if (classesValidationFunction)
        return classesValidationFunction(currentValue, parameterFormName);

      return true;
    },
    [
      classesValidationFunction,
      customValidation,
      initialValidationFunction,
      validationFunction,
    ],
  );
  const actualValidationRules = React.useMemo(() => {
    return {
      ...validationRules,
      ...(validationClass !== undefined
        ? classToValidate(validationClass)
        : {}),
    };
  }, [validationClass, validationRules]);

  return {
    actualValidationFunction,
    actualValidationRules,
  };
}
